"use client";

import { Text, SectionCard, CardTitle, CardDescription, Section } from "@/src/components";
import { cn } from "@/utils/utils";

interface ColorSwatchProps {
  name: string;
  token: string;
  className: string;
  textClass?: string;
  description?: string;
}

function ColorSwatch({ name, token, className, textClass = "text-foreground", description }: ColorSwatchProps) {
  return (
    <div className="space-y-2">
      <div
        className={cn(
          "h-20 rounded-lg border flex items-end p-3",
          className
        )}
      >
        <div className={cn("text-xs font-mono", textClass)}>{token}</div>
      </div>
      <div className="flex flex-col">
        <Text as="p" className="text-sm font-medium">{name}</Text>
        {description && (
          <Text as="p" className="text-xs text-muted-foreground">{description}</Text>
        )}
      </div>
    </div>
  );
}

const semanticColors = [
  { name: "Primary", token: "--primary", className: "bg-primary", textClass: "text-primary-foreground", description: "Primary actions, links, focus states" },
  { name: "Primary Foreground", token: "--primary-foreground", className: "bg-primary-foreground border-2", description: "Text on primary backgrounds" },
  { name: "Secondary", token: "--secondary", className: "bg-secondary", description: "Secondary actions" },
  { name: "Destructive", token: "--destructive", className: "bg-destructive", textClass: "text-white", description: "Errors, delete actions" },
  { name: "Muted", token: "--muted", className: "bg-muted", description: "Subdued backgrounds" },
  { name: "Muted Foreground", token: "--muted-foreground", className: "bg-muted-foreground", textClass: "text-white", description: "Secondary text, hints" },
];

const surfaceColors = [
  { name: "Background", token: "--background", className: "bg-background", description: "Page background" },
  { name: "Foreground", token: "--foreground", className: "bg-foreground", textClass: "text-background", description: "Default text color" },
  { name: "Card", token: "--card", className: "bg-card", description: "Card backgrounds" },
  { name: "Popover", token: "--popover", className: "bg-popover", description: "Dropdown/popup backgrounds" },
  { name: "Accent", token: "--accent", className: "bg-accent", description: "Hover/selected states" },
  { name: "Border", token: "--border", className: "bg-border", description: "Border color" },
];

const chartColors = [
  { name: "Chart 1", token: "--chart-1", className: "bg-chart-1", textClass: "text-white" },
  { name: "Chart 2", token: "--chart-2", className: "bg-chart-2", textClass: "text-white" },
  { name: "Chart 3", token: "--chart-3", className: "bg-chart-3", textClass: "text-foreground" },
  { name: "Chart 4", token: "--chart-4", className: "bg-chart-4", textClass: "text-foreground" },
  { name: "Chart 5", token: "--chart-5", className: "bg-chart-5", textClass: "text-foreground" },
];

const sidebarColors = [
  { name: "Sidebar", token: "--sidebar", className: "bg-sidebar", description: "Sidebar background" },
  { name: "Sidebar Foreground", token: "--sidebar-foreground", className: "bg-sidebar-foreground", textClass: "text-sidebar", description: "Sidebar text" },
  { name: "Sidebar Primary", token: "--sidebar-primary", className: "bg-sidebar-primary", textClass: "text-sidebar-primary-foreground", description: "Active item" },
  { name: "Sidebar Accent", token: "--sidebar-accent", className: "bg-sidebar-accent", description: "Hover state" },
];

export default function DocsTokensColors() {
  return (
    <>
      {/* Header */}
      <Section>
        <div className="space-y-4">
          <Text as="h1" className="text-h2 font-bold">
            Color Tokens
          </Text>
          <Text className="text-lg text-muted-foreground max-w-2xl">
            Colors use the OKLCH color space for perceptually uniform color manipulation
            and better dark mode support.
          </Text>
        </div>
      </Section>

      {/* Semantic Colors */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Semantic Colors</CardTitle>
              <CardDescription>Core colors that convey meaning across the interface</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {semanticColors.map((color) => (
              <ColorSwatch key={color.token} {...color} />
            ))}
          </div>
        </SectionCard>
      </Section>

      {/* Surface Colors */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Surface Colors</CardTitle>
              <CardDescription>Background and container colors</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {surfaceColors.map((color) => (
              <ColorSwatch key={color.token} {...color} />
            ))}
          </div>
        </SectionCard>
      </Section>

      {/* Chart Colors */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Chart Colors</CardTitle>
              <CardDescription>Colors for data visualization</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {chartColors.map((color) => (
              <ColorSwatch key={color.token} {...color} />
            ))}
          </div>
        </SectionCard>
      </Section>

      {/* Sidebar Colors */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Sidebar Colors</CardTitle>
              <CardDescription>Navigation sidebar-specific colors</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {sidebarColors.map((color) => (
              <ColorSwatch key={color.token} {...color} />
            ))}
          </div>
        </SectionCard>
      </Section>

      {/* Usage Guide */}
      <Section>
        <SectionCard
          header={<CardTitle>Usage</CardTitle>}
          contentClassName="p-6 space-y-4"
        >
          <div className="space-y-3">
            <Text className="font-semibold">In Tailwind:</Text>
            <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm">
              <code>{"<div className=\"bg-primary text-primary-foreground\" />"}</code>
            </div>
          </div>

          <div className="space-y-3">
            <Text className="font-semibold">In CSS:</Text>
            <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm space-y-1">
              <code className="block">{".element {"}</code>
              <code className="block pl-4">{"background: var(--primary);"}</code>
              <code className="block pl-4">{"color: var(--primary-foreground);"}</code>
              <code className="block">{"}"}</code>
            </div>
          </div>

          <div className="space-y-3">
            <Text className="font-semibold">In Style Drivers:</Text>
            <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm">
              <code>{"root: \"bg-primary text-primary-foreground hover:bg-primary/90\""}</code>
            </div>
          </div>
        </SectionCard>
      </Section>

      {/* Guidelines */}
      <Section>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <SectionCard
            header={<CardTitle className="text-success">Do</CardTitle>}
            contentClassName="p-6"
          >
            <ul className="space-y-2 text-sm">
              <li>- Use semantic colors consistently</li>
              <li>- Ensure WCAG AA contrast (4.5:1 minimum)</li>
              <li>- Test colors in both light and dark modes</li>
              <li>- Use destructive for error states only</li>
              <li>- Pair foreground with its background token</li>
            </ul>
          </SectionCard>

          <SectionCard
            header={<CardTitle className="text-destructive">Don't</CardTitle>}
            contentClassName="p-6"
          >
            <ul className="space-y-2 text-sm">
              <li>- Don't use red for non-error states</li>
              <li>- Don't create new colors without approval</li>
              <li>- Don't rely on color alone for meaning</li>
              <li>- Don't hardcode hex values in components</li>
              <li>- Don't mix color systems (use only tokens)</li>
            </ul>
          </SectionCard>
        </div>
      </Section>
    </>
  );
}
